import { useQuery } from '@tanstack/react-query';
import { getProjectView, listProjectViews } from '@/api/adapter/projectViews';
import { getProjectParticipants } from '@/api/adapter/projectParticipants';
import { getProjectBrief } from '@/api/adapter/projectBrief';
import { useAuthStore } from '@/stores/authStore';

// Separate current read models from the unmigrated mutation/service models.
export const currentProjectKeys = {
  list: (principal: string) => ['current-projects', principal, 'list'] as const,
  detail: (principal: string, id: string) =>
    ['current-projects', principal, 'detail', id] as const,
};
export function useProjectViews() {
  const principal = useAuthStore((state) => state.session?.principalId);
  return useQuery({
    queryKey: currentProjectKeys.list(principal ?? ''),
    queryFn: async ({ signal }) => {
      const projects = await listProjectViews(signal);
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload to view projects.');
      return projects;
    },
    enabled: !!principal,
    retry: false,
    staleTime: 30000,
  });
}
export function useProjectView(id: string | undefined) {
  const principal = useAuthStore((state) => state.session?.principalId);
  return useQuery({
    queryKey: currentProjectKeys.detail(principal ?? '', id ?? ''),
    queryFn: async ({ signal }) => {
      const project = await getProjectView(id ?? '', signal);
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload to view this project.');
      return project;
    },
    enabled: !!principal && !!id,
    retry: false,
    staleTime: 30000,
  });
}

export function useProjectBrief(id: string) {
  const principal = useAuthStore((state) => state.session?.principalId);
  return useQuery({
    queryKey: ['project-brief', principal, id],
    queryFn: async ({ signal }) => {
      const brief = await getProjectBrief(id, signal);
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload to view this brief.');
      return brief;
    },
    enabled: !!principal,
    retry: false,
    staleTime: 30000,
  });
}

export function useProjectParticipants(id: string) {
  const principal = useAuthStore((state) => state.session?.principalId);
  return useQuery({
    queryKey: ['project-participants', principal, id],
    queryFn: async ({ signal }) => {
      const participants = await getProjectParticipants(id, signal);
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload to view participants.');
      return participants;
    },
    enabled: !!principal,
    retry: false,
    staleTime: 30000,
  });
}
